import axiosInstance from "apiConfig";

// Status-related API calls
export const getStatuses = async ({ tenant_id }) => {
  try {
    const response = await axiosInstance.get("/status/statuses", {
      params: { tenant_id }, // Pass tenant_id to the API
    });

    // Ensure the API response is returned as is
    return response.data;
  } catch (error) {
    console.error("Error fetching statuses:", error);
    throw error; // Ensure errors are caught in the calling function
  }
};

export const updateStatusName = async (id, status_name, tenant_id) => {
  return await axiosInstance.put(`/status/statuses/${id}`, {
    status_name,
    tenant_id,
  });
};

export const updateStatus = async (id, statusName, color, tenantId) => {
  // console.log("Payload sent to API:", { id, statusName, color, tenantId }); // Debug log
  try {
    const response = await axiosInstance.put(`/status/update-status/${id}`, {
      status_name: statusName,
      color: color,
      tenant_id: tenantId,
    });
    return response.data;
  } catch (error) {
    console.error("Error updating status:", error.response?.data || error);
    throw error;
  }
};

export const addStatus = async (payload) => {
  const { tenant_id, name, color } = payload; // Extract properties from payload
  try {
    const response = await axiosInstance.post("/status/statuses", {
      tenant_id,
      name,
      color,
    });
    return response.data;
  } catch (error) {
    console.error("Error adding status:", error);
    throw error;
  }
};

export const getLeadSources = async (params) => {
  return await axiosInstance.get("/ref/lead-sources", { params });
};
export const createLeadSource = async (data) => {
  return await axiosInstance.post("/ref/lead-sources", data);
};
export const updateLeadSource = async (data) => {
  return await axiosInstance.put(`/ref/lead-sources/${data.id}`, data);
};

// Reference-related API calls
export const getReferences = async () => {
  return await axiosInstance.get("/ref/references");
};

export const createReference = async (reference_name) => {
  return await axiosInstance.post("/ref/references", { reference_name });
};

export const updateReference = async (id, reference_name) => {
  return await axiosInstance.put(`/ref/references/${id}`, { reference_name });
};

// Settings-related API calls
export const getSettings = async () => {
  try {
    const response = await axiosInstance.get(`/settings`);
    return response.data;
  } catch (error) {
    console.error("Error fetching settings:", error);
    throw error;
  }
};

export const updateSettings = async (formData) => {
  try {
    const response = await axiosInstance.post(`/settings`, formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    });
    return response.data;
  } catch (error) {
    console.error("Error updating settings:", error);
    throw error;
  }
};

// User Access Control (UAC) API calls

// Fetch all roles dynamically
export const fetchRoles = async () => {
  try {
    const response = await axiosInstance.get("/uac/roles"); // Updated endpoint for roles
    return response.data;
  } catch (error) {
    console.error("Error fetching roles:", error);
    throw error;
  }
};

// Fetch all users with roles
export const fetchUsers = async (tenantId) => {
  try {
    const response = await axiosInstance.get(`/uac/users`, {
      params: { tenant_id: tenantId }, // Pass tenant_id as query params if required
    });

    if (response.data && response.data.success) {
      return response.data.data; // Return the user data
    } else {
      throw new Error(response.data.message || "Failed to fetch users.");
    }
  } catch (error) {
    console.error("Error fetching users:", error);
    throw error; // Rethrow the error for the calling component to handle
  }
};
// Add a new user

export const fetchRms = async () => {
  try {
    const response = await axiosInstance.get("/uac/users"); // Fetch all users including RMs
    return response.data.filter((user) => user.role_name === "RM"); // Filter only RMs
  } catch (error) {
    console.error("Error fetching RM data:", error);
    throw error;
  }
};

// Send a welcome email
export const sendWelcomeMail = async (userEmail) => {
  try {
    const response = await axiosInstance.post("/mail/welcome", userEmail);
    return response.data;
  } catch (error) {
    console.error("Error sending welcome mail to the user:", error);
    throw error;
  }
};

export const assignManager = async (rm_id, manager_id) => {
  try {
    const response = await axiosInstance.put("/uac/assignmanager", {
      rm_id,
      manager_id,
    });
    return response.data;
  } catch (error) {
    console.error(
      "Error assigning manager:",
      error.response?.data || error.message
    );
    throw error;
  }
};

export const registerUser = async (userPayload) => {
  try {
    const response = await axiosInstance.post("/uac/create", userPayload); // Replace with the correct API endpoint
    return response.data;
  } catch (error) {
    console.error("Error during user registration:", error);
    throw error;
  }
};

export const updateStatusOrder = async (payload) => {
  // console.log("Payload in updateStatusOrder:", payload); // Log the incoming payload
  try {
    const response = await axiosInstance.post("/status/order", payload);
    return response.data;
  } catch (error) {
    console.error("Error updating status order:", error);
    throw error;
  }
};

export const getSubscription = async (tenantId) => {
  try {
    const response = await axiosInstance.get(
      `/subscription/get-subscription/${tenantId}`
    );
    return response.data;
  } catch (error) {
    console.error("Error fetching subscription:", error);
    throw error;
  }
};

// Get available subscription plans
export const getAvailablePlans = async () => {
  try {
    const response = await axiosInstance.get("/subscription/available-plans");
    return response.data;
  } catch (error) {
    console.error("Error fetching plans:", error);
    throw error;
  }
};

export const getCurrentPlan = async (tenantId) => {
  try {
    const response = await axiosInstance.get(
      `/subscription/get-currentplan/${tenantId}`
    );
    return response.data;
  } catch (error) {
    console.error("Error fetching current plan:", error);
    throw error;
  }
};

// Update subscription for the tenant
export const updateSubscription = async (tenantId, newPlanId) => {
  if (!tenantId || !newPlanId) {
    console.error(
      "Error: Missing required parameters in updateSubscription API."
    );
    return {
      success: false,
      message: "Tenant ID and Plan ID are required.",
    };
  }

  try {
  

    const response = await axiosInstance.post(
      `/subscription/change-plan/${tenantId}`,
      {
        newPlanId, // Ensure correct parameter is passed
      }
    );

    return response.data; // Return the backend response
  } catch (error) {
    console.error("Error in updateSubscription API:", error);
    return {
      success: false,
      message: error.response?.data?.message || "An error occurred.",
    };
  }
};
export const verifyUpgradePayment = async (paymentData) => {
  try {
    const response = await axiosInstance.post(
      "/subscription/verify-payment",
      paymentData
    );
    return response.data;
  } catch (error) {
    console.error("Error in verifyUpgradePayment API:", error);
    return {
      success: false,
      message: error.response?.data?.message || "Payment verification failed.",
    };
  }
};

export const saveDashboardSettings = async (settings) => {
  // Use localStorage as an example
  localStorage.setItem("dashboardSettings", JSON.stringify(settings));
};

// Retrieve dashboard settings
export const getDashboardSettings = async () => {
  const settings = localStorage.getItem("dashboardSettings");
  return settings ? JSON.parse(settings) : null;
};

export const fetchCategories = async () => {
  try {
    const response = await axiosInstance.get(`services/categories`);
    return response.data;
  } catch (error) {
    console.error("Error fetching categories:", error);
    throw error;
  }
};

// Fetch all products
export const fetchProducts = async () => {
  try {
    const response = await axiosInstance.get(`services/products`);
    return response.data;
  } catch (error) {
    console.error("Error fetching products:", error);
    throw error;
  }
};

// Add a new category
export const addCategory = async (data) => {
  try {
    const response = await axiosInstance.post(`services/categories`, data);
    return response.data;
  } catch (error) {
    console.error("Error adding category:", error);
    throw error;
  }
};

// Update a category
export const updateCategory = async (id, data) => {
  try {
    const response = await axiosInstance.put(`services/categories/${id}`, data);
    return response.data;
  } catch (error) {
    console.error("Error updating category:", error);
    throw error;
  }
};

// Delete a category
export const deleteCategory = async (id) => {
  try {
    const response = await axiosInstance.delete(`services/categories/${id}`);
    return response.data;
  } catch (error) {
    console.error("Error deleting category:", error);
    throw error;
  }
};

// Add a new product
export const addProduct = async (data) => {
  try {
    const response = await axiosInstance.post(`services/products`, data);
    return response.data;
  } catch (error) {
    console.error("Error adding product:", error);
    throw error;
  }
};

// Update a product
export const updateProduct = async (id, data) => {
  try {
    const response = await axiosInstance.put(`services/products/${id}`, data);
    return response.data;
  } catch (error) {
    console.error("Error updating product:", error);
    throw error;
  }
};

// Delete a product
export const deleteProduct = async (id) => {
  try {
    const response = await axiosInstance.delete(`services/products/${id}`);
    return response.data;
  } catch (error) {
    console.error("Error deleting product:", error);
    throw error;
  }
};

export const toggleUserStatus = async (userId, status) => {
  try {
    const response = await axiosInstance.put(
      `/uac/users/toggle-status/${userId}`,
      { status }
    );
    return response.data;
  } catch (error) {
    console.error("Error toggling user status:", error);
    throw error;
  }
};

export const updateUser = async (userPayload) => {
  try {
    const response = await axiosInstance.put(
      `/uac/users/update-user/${userPayload.user_id}`,
      userPayload
    );
    return response.data;
  } catch (error) {
    console.error(
      "Error updating user:",
      error.response?.data || error.message
    );
    throw error.response?.data || error;
  }
};

export const fetchManagers = async () => {
  try {
    const response = await axiosInstance.get("/uac/managers");
    return response.data;
  } catch (error) {
    console.error("Error fetching managers:", error);
    throw error;
  }
};

export const submitUpgradeRequest = async (
  tenantId,
  selectedPlan,
  comments
) => {
  try {
    const response = await axiosInstance.post("/subscription/upgrade-request", {
      tenant_id: tenantId,
      plan_id: selectedPlan,
      comments,
    });
    return response.data;
  } catch (error) {
    console.error("Error submitting upgrade request:", error);
    throw error;
  }
};


export const getInvoices = async (tenantId) => {  
  try {
    const response = await axiosInstance.get(`/subscription/get-invoices/${tenantId}`);
    return response.data;
  } catch (error) {
    console.error("Error fetching invoices:", error);
    throw error;
  }
};


export const getCurrencies = async () => {
 try {
  const response = await axiosInstance.get("/settings/currencies");
  return response.data;
 } catch (error) {
    console.error("Error fetching currencies:", error);
    throw error;
  }
  
 };


export const getPreferences = async () => {
try {
    const res = await axiosInstance.get("/settings/preferences");
    return res.data;
} catch (error) {
    console.error("Error fetching preferences:", error);
    throw error;
  
}
};

export const updatePreferences = async (formData) => {
  const plainData = Object.fromEntries(formData.entries());

  return await axiosInstance.put("/settings/preferences", plainData, {
    headers: {
      "tenant-id": plainData.tenant_id, // Send it explicitly
    },
  });
};

export const updateUserPermissions = async (userId, { permissionKey, value }) => {
  try {
    const response = await axiosInstance.put(
      `/uac/${userId}/permissions`,
      {
        permissionKey,
        value: Boolean(value) // Ensure value is boolean
      }
    );
    return response.data;
  } catch (error) {
    console.error("Error updating user permissions:", error.response?.data || error.message);
    throw error;
  }
};
export const getArchivedLeads = async (tenantId) => {
  try {
    const response = await axiosInstance.get("/leads/leads/archived", {
      headers: {
        "tenant-id": tenantId,
      },
    });
    return response.data;
  } catch (error) {
    console.error("Error fetching hidden leads:", error);
    throw error;
  }
};

export const unarchiveLead = async (leadId, tenantId) => {
  try {
    const response = await axiosInstance.put(
      `/leads/leads/${leadId}/unarchive`,
      { tenant_id: tenantId }
    );
    return response.data;
  } catch (error) {
    console.error("Error unhiding lead:", error);
    throw error;
  }
};

export const deleteStatus = async (statusId, tenantId) => { 
  try {
    const response = await axiosInstance.delete(`/status/status/${statusId}`, {
      headers: {
        "tenant-id": tenantId,
      },
    });
    return response.data;
  } catch (error) {
    // console.error("Error deleting status:", error);
    throw error;
  }
}

export const getIntegrations = async () => {
  try {
    const response = await axiosInstance.get("/int/integrations/{tenant_id}");
    return response.data;
  } catch (error) {
    console.error("Error fetching integrations:", error);
    throw error;
  }
}

export const getWebsiteIntegration = async () => {
  try {
    const response = await axiosInstance.get("/int/web");

    if (!response.data || typeof response.data !== "object" || Array.isArray(response.data)) {
      throw new Error("Invalid website integration data received");
    }

    return response.data; // plain integration object
  } catch (error) {
    console.error("Error fetching website integration:", error);
    throw error;
  }
};

export const createWebsiteIntegration = async (source) => {
  try {
    const response = await axiosInstance.post("/int/web", { source });
    return response.data;
  } catch (error) {
    console.error("Error creating website integration:", error);
    throw error;
  }
}

// Custom field definition CRUD
export const getCustomFieldDefinitions = async (tenantId) => {
  try {
    const response = await axiosInstance.get("/custom-fields/definitions", {
      params: { tenant_id: tenantId },
    });
    return response.data;
  } catch (error) {
    console.error("Error fetching custom field definitions:", error);
    throw error;
  }
};

export const addCustomFieldDefinition = async ({ tenant_id, field_name }) => {
  try {
    const response = await axiosInstance.post("/custom-fields/definitions", {
      tenant_id,
      field_name,
    });
    return response.data;
  } catch (error) {
    console.error("Error adding custom field definition:", error);
    throw error;
  }
};

export const updateCustomFieldDefinition = async (id, field_name, tenantId) => {
  try {
    const response = await axiosInstance.put(`/custom-fields/definitions/${id}`, {
      field_name,
      tenant_id: tenantId,
    });
    return response.data;
  } catch (error) {
    console.error("Error updating custom field definition:", error);
    throw error;
  }
};

export const deleteCustomFieldDefinition = async (id, tenantId) => {
  try {
    const response = await axiosInstance.delete(`/custom-fields/definitions/${id}`, {
      headers: { "tenant-id": tenantId },
    });
    return response.data;
  } catch (error) {
    console.error("Error deleting custom field definition:", error);
    throw error;
  }
};